import type { DigitilesExtension } from '../catalog';
import { extensionSDK, type ExtensionContributions, type ExtensionFactory } from './sdk';
import type { PackageContent } from './manifest';

export interface LoadedPackage {
  manifest: DigitilesExtension;
  dispose(): void;
}
export type PackageLoader = (content: PackageContent) => Promise<LoadedPackage>;
/** Trusted, explicitly enabled code. This loader is not a sandbox. */
export const loadPackageCode: PackageLoader = async ({ manifest, files }) => {
  const urls: string[] = [];
  const dispose = () => urls.splice(0).forEach((url) => URL.revokeObjectURL(url));
  const parts: ExtensionContributions[] = [];
  const metadata = Object.freeze({
    ...manifest,
    capabilities: [...manifest.capabilities],
    entrypoints: [...manifest.entrypoints],
  });
  try {
    for (const path of manifest.entrypoints) {
      const url = URL.createObjectURL(
        new Blob([files[path]], { type: 'text/javascript' }),
      );
      urls.push(url);
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const part = await Promise.race([
          (async () => {
            const module = await import(/* @vite-ignore */ url);
            if (typeof module.default !== 'function')
              throw new Error(`${path} must export a default extension factory.`);
            return (module.default as ExtensionFactory)(extensionSDK, metadata);
          })(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(
              () => reject(new Error(`${path} did not finish loading within 5 seconds.`)),
              5000,
            );
          }),
        ]);
        if (!part || typeof part !== 'object')
          throw new Error(`${path} must return extension contributions.`);
        for (const key of Object.keys(part))
          if (!['tiles', 'nodes', 'chips', 'activate'].includes(key))
            throw new Error(`${path} returned unsupported contribution: ${key}`);
        for (const key of ['tiles', 'nodes', 'chips'] as const)
          if (part[key] !== undefined && !Array.isArray(part[key]))
            throw new Error(`${path}: ${key} must be an array.`);
        if (part.activate !== undefined && typeof part.activate !== 'function')
          throw new Error(`${path}: activate must be a function.`);
        parts.push(part);
      } finally {
        clearTimeout(timer);
      }
    }
    const activators = parts
      .map((part) => part.activate)
      .filter((fn): fn is NonNullable<DigitilesExtension['activate']> => !!fn);
    return {
      dispose,
      manifest: {
        id: manifest.id,
        name: manifest.name,
        description: manifest.description,
        version: manifest.version,
        capabilities: manifest.capabilities,
        tiles: parts.flatMap((part) => part.tiles ?? []),
        nodes: parts.flatMap((part) => part.nodes ?? []),
        chips: parts.flatMap((part) => part.chips ?? []),
        ...(activators.length
          ? {
              activate: (app, signal) => {
                const cleanups: (() => void)[] = [];
                try {
                  for (const activate of activators) {
                    const cleanup = activate(app, signal);
                    if (typeof cleanup !== 'function')
                      throw new Error('Package activate must return a cleanup function.');
                    cleanups.push(cleanup);
                  }
                } catch (error) {
                  for (const cleanup of cleanups.reverse()) {
                    try {
                      cleanup();
                    } catch {
                      /* Retain the activation error. */
                    }
                  }
                  throw error;
                }
                return () => {
                  let failure: unknown;
                  for (const cleanup of cleanups.reverse()) {
                    try {
                      cleanup();
                    } catch (error) {
                      failure = error;
                    }
                  }
                  if (failure) throw failure;
                };
              },
            }
          : {}),
      },
    };
  } catch (error) {
    dispose();
    throw error;
  }
};
